import { Routes } from '@angular/router';
import { loginGuard } from './core/guards/login/login.guard';
import { authGuard } from './core/guards/auth/auth.guard';
import { workspaceGuard } from './views/workspace/guards/workspace.guard';
import { dashboardResolver } from './api/bizzmine/dashboard/resolvers/dashboard.resolver';
import { UserSettingsResolver } from './api/bizzmine/user-settings/resolvers/user-settings.resolver';
import { UserSettingsIndexResolver } from './api/bizzmine/user-settings/resolvers/user-settings-index.resolver';
import { WorkspacesResolver } from './api/bizzmine/workspace/resolvers/workspaces/workspaces.resolver';
import { NotificationsResolver } from './api/bizzmine/notification/resolvers/notifications.resolver';
import { settingsGuard } from './core/guards/settings/settings.guard';
import {
  dispatchReadOnlyGuard,
  dispatchRevisionGuard,
  dispatchTaskGuard
} from './views/workspace/guards/dispatch-read-only-guard';
import { unsavedChangesGuard } from './features/bizzmine/form/guards/unsaved-changes.guard';
import { LayoutComponent } from 'src/app/shared/components/ui/layout/layout.component';
import { ReportComponent } from './views/report/report/report.component';
import { reportGuard } from './views/report/report.guard';
import {
  widgetWorkspaceItemResolver
} from 'src/app/api/bizzmine/widget/resolvers/widget-workspace-item.resolver';


export const routes: Routes = [
  {
    path: 'report/:reportId/instance/:instanceId/version/:versionId/theme/:themeId/userid/:userid/guid/:guid',
    canActivate: [reportGuard],
    component: ReportComponent
  },
  {
    path: 'report/:reportId/collection/:collectionId/list/:listId/folder/:folderId/searchfilter/:searchfilterId/parentfilter/:parentfilterId/theme/:themeId/userid/:userid/hash/:hash',
    canActivate: [reportGuard],
    component: ReportComponent
  },
  {
    path: 'settings',
    canActivate: [settingsGuard],
    loadComponent: () => import('./views/settings/settings.component').then(m => m.SettingsComponent)
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () => import('./views/auth/login/login.component').then(m => m.LoginComponent),
        canActivate: [
          loginGuard
        ]
      },
      {
        path: 'logout',
        loadComponent: () => import('./views/auth/logout/logout.component').then(m => m.LogoutComponent)
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login'
      }
    ]
  },
  {
    path: 'workspace',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: ':workspaceId',
        component: LayoutComponent,
        data: {
          settings: false
        },
        children: [
          {
            path: 'form',
            canDeactivate: [unsavedChangesGuard],
            loadChildren: () => import('./views/workspace/forms/form-landing-routes').then(m => m.formRoutes)
          },
          {
            path: 'dashboard',
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: ':workspaceItemId',
                loadComponent: () => import('./views/workspace/dashboard/dashboard.component').then(m => m.DashboardComponent),
                canDeactivate: [unsavedChangesGuard],
                resolve: {
                  dashboard: dashboardResolver
                }
              }
            ]
          },
          {
            path: 'widget',
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: ':workspaceItemId',
                loadComponent: () => import('./views/workspace/widget-workspace-item/widget-workspace-item.component').then(m => m.WidgetWorkspaceItemComponent),
                canDeactivate: [unsavedChangesGuard],
                resolve: {
                  workspaceItem: widgetWorkspaceItemResolver
                }
              }
            ]
          },
          {
            path: 'playground',
            loadComponent: () => import('./views/workspace/playground/playground.component').then(m => m.PlaygroundComponent)
          },
          {
            path: 'user-settings',
            loadComponent: () => import('./views/workspace/user-settings/user-settings.component').then(m => m.UserSettingsComponent),
            resolve: {
              settings: UserSettingsResolver,
              index: UserSettingsIndexResolver,
              workspaces: WorkspacesResolver
            }
          },
          {
            path: 'notifications',
            loadComponent: () => import('./views/workspace/notifications/notifications.component').then(m => m.NotificationsComponent),
            resolve: {
              notifications: NotificationsResolver
            }
          },
          {
            path: 'tasks',
            loadComponent: () => import('./views/workspace/tasks/tasks.component').then(m => m.TasksComponent)
          },
          {
            path: 'reminders',
            loadComponent: () => import('./views/workspace/reminders/reminders.component').then(m => m.RemindersComponent)
          },
          {
            path: 'flow-status/collectionId/:collectionId/instanceId/:instanceId/versionId/:versionId/methodType/:methodType',
            loadComponent: () => import('./views/workspace/flow-status/flow-status.component').then(m => m.FlowStatusComponent)
          },
          {
            path: 'flowstatus/collectionsid/:collectionId/instancesid/:instanceId/versionsid/:versionId/methodtype/:methodType',
            loadComponent: () => import('./views/workspace/flow-status/flow-status.component').then(m => m.FlowStatusComponent)
          },
          {
            path: 'history/collectionId/:collectionId/instanceId/:instanceId/versionId/:versionId',
            loadComponent: () => import('./views/workspace/history/history.component').then(m => m.HistoryComponent)
          }
        ]
      },
      {
        path: '',
        pathMatch: 'full',
        component: LayoutComponent,
        canActivate: [
          workspaceGuard
        ]
      }
    ]
  },
  {
    path: 'trn',
    children: [
      {
        path: ':trainingAppID/exam/:examSessionVersionsID',
        loadComponent: () => import('./views/training/training-exam-page/training-exam-page.component').then(m => m.TrainingExamPageComponent)
      }
    ]
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'auth'
  },
  {
    canActivate: [authGuard, dispatchReadOnlyGuard],
    path: 'collection/:collectionId/instance/:instanceId/version/:versionId/readonly',
    loadComponent: () => import('./views/workspace/forms/form-readonly-page/form-readonly-page.component').then(m => m.FormReadonlyPageComponent)
  },
  {
    canActivate: [authGuard, dispatchTaskGuard],
    path: 'task/:taskId',
    loadComponent: () => import('./views/workspace/forms/task-landing-page/task-landing-page.component').then(m => m.TaskLandingPageComponent)
  },
  {
    canActivate: [authGuard, dispatchRevisionGuard],
    path: 'revisiontask/:taskId',
    loadComponent: () => import('./views/workspace/forms/revision-landing-page/revision-landing-page.component').then(m => m.RevisionLandingPageComponent)
  },
  {
    canActivate: [authGuard],
    path: 'direct/collection/:collectionId/instance/:instanceId',
    loadComponent: () => import('./views/workspace/forms/direct-link-page/direct-link-page.component').then(m => m.DirectLinkPageComponent)
  },
  {
    path: 'Account/ExternalUser',
    loadComponent: () => import('./views/workspace/external-user/external-user-token/external-user-token.component').then(m => m.ExternalUserTokenComponent)
  },
  {
    path: 'Account/Form',
    loadComponent: () => import('./views/workspace/external-user/anonymous-user/anonymous-user.component').then(m => m.AnonymousUserComponent)
  },
  {
    path: ':languageCode/Account/Forms/:caption/:guid',
    loadComponent: () => import('./views/workspace/external-user/anonymous-portal/anonymous-portal.component').then(m => m.AnonymousPortalComponent)
  },
  {
    path: 'Account/Forms/:caption/:guid',
    loadComponent: () => import('./views/workspace/external-user/anonymous-portal/anonymous-portal.component').then(m => m.AnonymousPortalComponent)
  },
  {
    path: 'portal/form',
    loadComponent: () => import('./views/workspace/external-user/anonymous-user/anonymous-user.component').then(m => m.AnonymousUserComponent)
  },
  {
    path: ':languageCode/portal/:caption/:guid',
    loadComponent: () => import('./views/workspace/external-user/anonymous-portal/anonymous-portal.component').then(m => m.AnonymousPortalComponent)
  },
  {
    path: 'portal/:caption/:guid',
    loadComponent: () => import('./views/workspace/external-user/anonymous-portal/anonymous-portal.component').then(m => m.AnonymousPortalComponent)
  },
  {
    path: 'collection/:collectionId/instance/:instanceId',
    loadComponent: () => import('./views/workspace/forms/direct-link-page/direct-link-page.component').then(m => m.DirectLinkPageComponent)
  },
  {
    path: 'offline',
    loadComponent: () => import('./views/offline/offline-page.component').then(m => m.OfflinePageComponent)
  },
  { //redirect invalid urls to the default workspace
    path: '**',
    redirectTo: 'workspace'
  }
];